import React, { useCallback } from 'react'
import Table from 'components/Table'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import EditIcon from '@mui/icons-material/Edit'

import { color } from 'styles/colors'
import { screenSize, Circle } from 'styles/shared'
import StatusCell from 'pages/shared/StatusCell'
import { Metric } from '.'

import { generateUniqueId } from 'helpers/formatters'

interface props {
  deviceMetrics: Metric[]
}

const MetricsTable = ({ deviceMetrics = [] }: props) => {
  const navigate = useNavigate()

  const ChartList = useCallback((charts) => {
    let classNames = ''
    if (charts.length !== 0) {
      classNames = charts.map(function (chart) {
        if (chart.class === null) {
          return 'All'
        } else {
          return chart.class
        }
      }).join(', ')
    } else {
      classNames = 'None'
    }
    return classNames
  }, [])

  const sortedDeviceMetrics = () => {
    return deviceMetrics.sort((a, b) => {
      if (a.draft === b.draft) {
        return a.id.localeCompare(b.id)
      } else {
        return a.draft ? 1 : -1
      }
    })
  }

  return (
    <Table
        items={sortedDeviceMetrics()}
        noDataMessage={{
          title: 'No Device Metric',
          description: ''
        }}
    >
      <S.TableHeader>
        <S.HeaderCell title="ID" />
        <S.HeaderCell title="Name" />
        <S.HeaderCell title="Color" />
        <S.HeaderCell title="Description" />
        <S.HeaderCell title="Call to Actions" />
        <S.HeaderCell title="Unit" />
        <S.HeaderCell title="Charts" />
        <S.HeaderCell title="Published Status" />
      </S.TableHeader>

      {sortedDeviceMetrics().map(metric => (
        <div key={metric.id}>
          <S.TableRow>
            <S.Cell>
              <S.SmallScreenLabel>ID:</S.SmallScreenLabel>
              <S.MetricId aria-label="id">{metric.id}</S.MetricId>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Name:</S.SmallScreenLabel>
              <p>{metric.name}</p>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Color:</S.SmallScreenLabel>
              <S.Color>
                <Circle color={metric.color} />
                <p>{metric.color}</p>
              </S.Color>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Description:</S.SmallScreenLabel>
              <p>{metric.description}</p>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Call to Actions:</S.SmallScreenLabel>
              <S.List itemsCount={metric.callToAction?.length ?? 0}>
                {metric?.callToAction?.length > 0
                  ? metric.callToAction.map(({ description, details }) =>
                    (
                      <div key={generateUniqueId()}>
                        <S.Li>{description}</S.Li>
                        {details && <S.DetailsList> {details.map(detail => <S.Li key={generateUniqueId()}>{detail}</S.Li>)}</S.DetailsList>}
                      </div>
                    )
                  )
                  : '--'}
              </S.List>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Unit:</S.SmallScreenLabel>
              <p>{metric.unit}</p>
            </S.Cell>
            <S.Cell>
              <S.SmallScreenLabel>Charts:</S.SmallScreenLabel>
              <p>{ChartList(metric.charts)}</p>
            </S.Cell>
            <S.Cell>
              <div>
                <S.SmallScreenLabel>Published Status:</S.SmallScreenLabel>
                <StatusCell isEnabled={metric.draft} />
                <S.EditIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/device_metrics/${metric.id}/edit`)}
                    aria-label={`Edit Device metric with title: ${metric.name}`}
                >
                  Edit
                </S.EditIcon>
              </div>
            </S.Cell>
          </S.TableRow>
        </div>
      ))}
    </Table>
  )
}

const S = {
  HeaderCell: styled(Table.HeaderCell)`
      flex: 1;
    `,
  TableHeader: styled(Table.Header)`
        position: sticky;
        top: 15px;
    `,
  TableRow: styled(Table.Row)`
      align-items: center;
    `,
  Cell: styled(Table.Cell)`
      flex: 1;
      flex-direction: column;
      line-height: 24px;
      text-overflow: ellipsis;
      padding: 20px 8px;
      overflow: hidden;
  
      @media ${screenSize.medium} {
        line-height: inherit;
        padding: 10px 8px;
      }
      @media ${screenSize.small} {
        line-height: inherit;
        padding: 10px 5px;
      }
  
      @media ${screenSize.small} {
        display: inline-block;
        width: 100%;
        padding: 5px;
        line-height: 20px;
      }
    `,
  Color: styled.div`
      display: flex;
      align-items: center;
    `,
  MetricId: styled.p`
      overflow-wrap: break-word;
    `,
  StatusCell: styled(Table.Cell)`
      flex: 1;
      flex-direction: column;
  
  
      @media ${screenSize.small} {
        display: inline-block;
        width: 50%;
        padding-left: 5px;
      }
    `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })<{ enabled: boolean }>`
      display: inline-block;
      padding: 4px 10px;
      margin-bottom: 5px;
      text-align: center;
      font-size: 12px;
      letter-spacing: 0;
      text-transform: uppercase;
      border: 1px solid ${color('border')};
      border-radius: 14px;
      margin-right: 10px;
  
      ${({ enabled }) => {
    if (enabled) {
      return `
          color: ${color('siteMessageStatusDisabledText')};
        `
    } else {
      return `
          border-color: ${color('siteMessageEnabledStatusBorder')};
          background-color: ${color('positiveStatus')};
          color: ${color('siteMessageStatusEnabledText')};
        `
    }
  }}
    `,
  SmallScreenLabel: styled.div`
      display: none;
      font-weight: bold;
  
      @media ${screenSize.small} {
        display: block;
        padding-right: 10px;
      }
    `,
  EditIcon: styled(EditIcon)`
      color: ${color('EditIcon')};
    `,
  List: styled.ul.withConfig({
    shouldForwardProp: (prop) => !['itemsCount'].includes(prop)
  })<{ itemsCount: number }>`
      list-style-type: ${({ itemsCount }) => itemsCount > 1 ? 'disc' : 'none'};
      list-style-position: inside;
      color: ${color('tooltipText')};
      padding-bottom: 10px;
      padding-left: 10px;
      margin: 0;
    `,
  DetailsList: styled.ul`
      list-style-position: inside;
      padding-left: 10px;
    `,
  Li: styled.li`
      color: ${color('tooltipText')};
    `
}

export default MetricsTable
